import React, { useState, useRef, useEffect } from 'react';

const OTPInput = ({ value, onChange, onKeyPress, disabled = false, length = 6 }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRefs = useRef([]);

    useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);

    const handleChange = (e, index) => {
        const val = e.target.value;

        // Only allow single digits
        if (val.length > 1) return;

        // Only allow numbers
        if (val && !/^\d$/.test(val)) return;

        const newValue = value.split('');
        newValue[index] = val;

        const newOtp = newValue.join('');
        onChange(newOtp);

        // Auto-advance to next input
        if (val && index < length - 1) {
            setActiveIndex(index + 1);
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        // Handle backspace
        if (e.key === 'Backspace') {
            e.preventDefault();

            const newValue = value.split('');

            if (newValue[index]) {
                // Clear current input
                newValue[index] = '';
                onChange(newValue.join(''));
            } else if (index > 0) {
                // Move to previous input and clear it
                newValue[index - 1] = '';
                onChange(newValue.join(''));
                setActiveIndex(index - 1);
                inputRefs.current[index - 1]?.focus();
            }
        }
        // Handle arrow keys
        else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            setActiveIndex(index - 1);
            inputRefs.current[index - 1]?.focus();
        }
        else if (e.key === 'ArrowRight' && index < length - 1) {
            e.preventDefault();
            setActiveIndex(index + 1);
            inputRefs.current[index + 1]?.focus();
        }
        // Handle paste
        else if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            // Allow paste event to be handled by onPaste
        }
        // Pass through Enter key for parent component
        else if (e.key === 'Enter') {
            onKeyPress && onKeyPress(e);
        }
    };

    const handleFocus = (index) => {
        setActiveIndex(index);
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData('text');

        // Only allow numeric paste data
        if (!/^\d+$/.test(pasteData)) return;

        const pasteLength = Math.min(pasteData.length, length);
        const newValue = pasteData.slice(0, pasteLength).padEnd(length, '');

        onChange(newValue.slice(0, length));

        // Focus the next empty input or the last input
        const nextIndex = Math.min(pasteLength, length - 1);
        setActiveIndex(nextIndex);
        inputRefs.current[nextIndex]?.focus();
    };

    const handleClick = (index) => {
        setActiveIndex(index);
        inputRefs.current[index]?.focus();
    };

    return (
        <div className="flex justify-center space-x-2">
            {Array.from({ length }, (_, index) => (
                <input
                    key={index}
                    ref={(ref) => (inputRefs.current[index] = ref)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={value[index] || ''}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={() => handleFocus(index)}
                    onClick={() => handleClick(index)}
                    onPaste={handlePaste}
                    disabled={disabled}
                    className={`
                        w-12 h-12 text-center text-lg font-semibold
                        border-2 rounded-lg
                        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500
                        transition-all duration-200
                        ${disabled
                            ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                            : value[index]
                                ? 'border-orange-500 bg-orange-50 text-orange-900'
                                : activeIndex === index
                                    ? 'border-orange-300 bg-white'
                                    : 'border-gray-300 bg-white hover:border-gray-400'
                        }
                    `}
                    autoComplete="one-time-code"
                    aria-label={`Verification code digit ${index + 1}`}
                />
            ))}
        </div>
    );
};

export default OTPInput; 